import queryString from "query-string";
import { useMemo } from "react";
import { useApiClient, useBackofficeClient } from "../../../../lib/http";
import { regenPropertyCache } from "../../../common/api";
import { Image, Translation } from "../../../shared/widgets/ImageManager";

interface MediaTranslationDto {
  locale: string;
  title: string;
  description: string;
}

interface MediaDto {
  id: string;
  filename: string;
  url: string;
  translations: MediaTranslationDto[];
  labels: string[];
  caption: string;
}

function mapTranslation(translations: Translation): MediaTranslationDto[] {
  return Object.keys(translations).map(locale => ({
    locale,
    title: translations[locale].title,
    description: translations[locale].description || "",
  }));
}

function translationsFromDto(translations: MediaTranslationDto[] = []): Translation {
  return translations.reduce((acc, { locale, title, description }) => {
    acc[locale] = { title, description };
    return acc;
  }, {} as Translation);
}

function imageFromDto(dto: MediaDto): Image {
  return Object.assign(new File([""], dto.filename), {
    id: dto.id,
    filename: dto.filename,
    url: dto.url,
    translations: translationsFromDto(dto.translations),
    labels: dto.labels,
    caption: dto.caption,
  } as Image);
}

export function useBackofficeApi(propertyTicker: string) {
  const httpClient = useBackofficeClient();
  const httpClientCache = useApiClient();

  return useMemo(
    () => ({
      regenCache: async () => {
        await regenPropertyCache(httpClientCache, propertyTicker);
      },
      createImages: async (accommodationId: string, data: Image[] = []): Promise<Image[]> => {
        if (!data.length) {
          return [];
        }
        const formData = new FormData();
        data.forEach(it => {
          formData.append("images", it, it.id);
          formData.append(
            "medias",
            new Blob(
              [
                JSON.stringify({
                  id: it.id,
                  filename: it.filename.toLowerCase(),
                  translations: it.translations ? mapTranslation(it.translations) : [],
                }),
              ],
              {
                type: "application/json",
              }
            )
          );
        });
        const response = await httpClient.post<FormData, MediaDto[]>(
          `/v1/${propertyTicker}/accommodation/${accommodationId}/media/multi`,
          formData
        );
        return (response || []).map(imageFromDto);
      },
      removeImages: async (accommodationId: string, data: Image[]) => {
        if (data.length) {
          const request = data.map(d => d.id);
          await httpClient.delete<any>(
            `/v1/${propertyTicker}/accommodation/${accommodationId}/media?${queryString.stringify({
              mediaIds: request,
            })}`
          );
        }
      },
      updateImages: async (accommodationId: string, data: Image[]) => {
        if (data.length) {
          const request: MediaDto[] = data.map(it => ({
            id: it.id,
            filename: it.filename,
            url: it.url,
            translations: it.translations ? mapTranslation(it.translations) : [],
            labels: it.labels ?? [],
            caption: it.caption ?? "",
          }));
          await httpClient.put<MediaDto[], MediaDto[]>(
            `/v1/${propertyTicker}/accommodation/${accommodationId}/media`,
            request
          );
        }
      },
    }),
    [httpClient, httpClientCache, propertyTicker]
  );
}
